import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  ResendDashboardControllerService,
  Edition,
  EditionControllerService,
  ResendDashboardCount,
  ResendStatusCount,
  CompositionControllerService,
  CompositionDashboardControllerService,
  CompositionEditionStatusTotal
} from 'src/app/admin-api';
import { TableModalComponent } from 'src/app/components/table-modal/table-modal.component';
import { DropdownFilter, TableColumn } from 'src/app/components/table';
import {
  CompositionStatusCounter,
  EditionId,
  SubscriptionId,
  getAllSubscriptionsIncludeAll
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-resend-dashboard',
  templateUrl: './resend-dashboard.component.html',
  styleUrl: './resend-dashboard.component.scss',
  providers: [DatePipe, DialogService]
})
export class ResendDashboardComponent implements OnInit {
  private compositionService = inject(ResendDashboardControllerService);
  private compositionsDashboardService = inject(CompositionControllerService);
  private compositionDashboardService = inject(CompositionDashboardControllerService);
  private editionService = inject(EditionControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private datePipe = inject(DatePipe);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);

  form = new FormGroup({
    subscriptionId: new FormControl<number>(1, [
      Validators.required,
      Validators.min(0)
    ]),
    editionId: new FormControl<number>(
      Number(
        `${new Date().getFullYear()}${(new Date().getMonth() + 1)
          .toString()
          .padStart(2, '0')}`
      ),
      [Validators.required, Validators.min(1)]
    )
  });

  subscriptions = getAllSubscriptionsIncludeAll().filter(
    (s) => !SubscriptionId.isSubscription(s.value)
  );
  editions: Array<Edition> | undefined;
  editionStatusTotals: Array<CompositionEditionStatusTotal> | undefined;
  subscriberEditionStatusList: Array<CompositionStatusCounter> | undefined;
  resendCounts: ResendDashboardCount | undefined;
  alertCounts: ResendDashboardCount | undefined;
  resendAlertCounts: ResendDashboardCount | undefined;
  editionLoading = false;
  alertLoading = false;
  resendLoading = false;
  resendAlertLoading = false;
  ready = false;

  private resendStatusOrder: Record<string, number> = {
    'AG. ESTOQUE': 1,
    'AG PORTFOLIO': 2,
    'AG. APROVAÇÃO': 3,
    'Confirmado': 4,
    'Preparando pedido': 5,
    'Enviado': 6,
    'Aguardando Nota': 7,
    'Aguardando Rastreio': 8,
    'Em transporte': 9,
    'Entregue': 10,
    'Cancelado': 11,
    'Falha na entrega': 12,
    'Pedido suspenso': 13,
    'Substituído': 14
  };

  resendStatusTooltips: Record<string, string> = {
    'AG. ESTOQUE': '1. Ag. Estoque',
    'AG PORTFOLIO': '1. Ag. Portfolio',
    'AG. APROVAÇÃO': '1. Ag. Aprovação',
    'Confirmado': '1. Ag. Assinatura<br>2. Realocar',
    'Preparando pedido': '1. Ag. Ressuprimento<br>2. Ag. Liberar Edições<br>3. Ag. Faturamento<br>4. Ag. Embarque',
    'Enviado': '1. Enviado',
    'Aguardando Nota': '1. Aguardando Nota',
    'Aguardando Rastreio': '1. Aguardando Rastreio',
    'Em transporte': '1. Despachado<br>2. Ag. Retirada<br>3. Ag. Entrega<br>4. Em Trânsito<br>5. Ag. Confirmação de Entrega<br>6. Em Devolução<br>7. Devolvido',
    'Entregue': '1. Entregue',
    'Falha na entrega': '1. Falha na Entrega<br>2. Averiguar Falha na Entrega',
    'Cancelado': '1. Cancelado',
    'Pedido suspenso': '1. Pedido Suspenso',
    'Substituído': '1. Substituído'
  };

  editionStatusTooltips: Record<string, string> = {
    'Total assinantes': 'Soma de todos os assinantes da edição',
    'Aguardando alocação': 'Assinantes ainda não vinculados a uma composição/status'
  };

  editionStatusTooltip(name: string): string {
    return this.editionStatusTooltips[name] ?? ('1. ' + name);
  }

  alertTooltips: Record<string, string> = {
    'Em atraso': 'Pedidos com prazo de entrega vencido (exclui entregues e cancelados)',
    'Sem movimentação': 'Pedidos em status de transporte sem nenhuma atualização de rastreio da transportadora',
    'Entregas com ocorrências': '1. Cliente Ausente<br>2. Cliente Ausente 1<br>3. Cliente Ausente1<br>4. Cliente Ausente2<br>5. Cliente Ausente3<br>6. Endereço Não Localizado<br>7. Endereço Do Cliente Destino Não Localizado<br>8. Endereço Errado<br>9. Cliente Desconhecido<br>10. Aguardando Retirada<br>11. Objeto Aguardando Retirada No Endereço Indicado<br>12. Recusado Pelo Cliente<br>13. Recusado Pelo Destinatário<br>14. Estabelecimento Fechado<br>15. Área De Difícil Acesso<br>16. Area De Difícil Acesso<br>17. Reentrega Autorizada<br>18. Reentrega Solicitada Pelo Cliente<br>19. Aguardando Ação Do Remetente<br>20. Chuvas Na Regiao/Desastres Naturais<br>21. Veículo Não Cumpriu A Rota',
    'Entregas com falhas': '1. Em Devolução<br>2. Em Rota De Devolucao<br>3. Resolvido Para Devolucao<br>4. Devolução Não Autorizada Pelo Cliente<br>5. Devolvido<br>6. Devolvido Para B4A<br>7. Mercadoria Devolvida Ao Cliente De Origem<br>8. Extravio Total<br>9. Extravio<br>10. Avaria<br>11. Sinistro<br>12. Mudou-Se<br>13. Cliente Destino Mudou De Endereço<br>14. Fora De Abrangência<br>15. Em Análise De Perda<br>16. Em Análise<br>17. Retido Na Fiscalização<br>18. Em Processo De Busca'
  };

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
      LoaderService.showLoader();
      if (params['editionId']) {
        this.ready = false;
        this.form.patchValue({
          editionId: Number(params['editionId']),
          subscriptionId: EditionId.subscriptionId(Number(params['editionId']))
        });
        await this.findEditions();
        await this.findPage();
        this.title.setTitle(
          'Acompanhamento de Edições e Reenvios - ' + (this.edition?.theme || '')
        );
        LoaderService.showLoader(false);
      } else {
        this.navigate();
      }
    });
  }

  navigate(): void {
    this.router.navigate([
      '/operations/resend-dashboard/' + this.form.value.editionId
    ]);
  }

  async findPage(): Promise<void> {
    this.editionStatusTotals = undefined;
    this.subscriberEditionStatusList = undefined;
    this.resendCounts = undefined;
    this.alertCounts = undefined;
    this.resendAlertCounts = undefined;
    this.ready = true;

    this.loadCompositionDashboard();
    this.loadAlertCounts();
    this.loadResendCounts();
    this.loadResendAlertCounts();
  }

  private async loadCompositionDashboard(): Promise<void> {
    this.editionLoading = true;
    try {
      this.editionStatusTotals = await lastValueFrom(
        this.compositionDashboardService
          .findEditionStatusTotals(this.form.value.editionId)
          .pipe(map((data) => data.result))
      );
      this.subscriberEditionStatusList = [];
      const grouped = new Map<string, CompositionStatusCounter>();
      for (const t of this.editionStatusTotals.filter((t) => t.subscriberEditionStatusId)) {
        const name = t.subscriberEditionStatusName;
        const existing = grouped.get(name);
        if (existing) {
          existing.total = (existing.total || 0) + (t.subscribers || 0);
          existing.pending = (existing.pending || 0) + (t.pending || 0);
          existing.statusList.push({ id: t.subscriberEditionStatusId });
        } else {
          grouped.set(name, {
            id: t.subscriberEditionStatusId,
            status: name,
            total: t.subscribers || 0,
            pending: t.pending || 0,
            statusList: [{ id: t.subscriberEditionStatusId }]
          });
        }
      }
      this.subscriberEditionStatusList = Array.from(grouped.values());
      this.subscriberEditionStatusList.sort((s1, s2) => {
        if (s1.id > 0 && s2.id < 0) return -1;
        if (s1.id < 0 && s2.id > 0) return 1;
        return s1.id - s2.id;
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    this.editionLoading = false;
  }

  private async loadAlertCounts(): Promise<void> {
    this.alertLoading = true;
    try {
      this.alertCounts = await lastValueFrom(
        this.compositionService
          .findAlertDashboardCounts(this.form.value.editionId)
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    this.alertLoading = false;
  }

  private async loadResendCounts(): Promise<void> {
    this.resendLoading = true;
    try {
      this.resendCounts = await lastValueFrom(
        this.compositionService
          .findResendDashboardCounts(this.form.value.editionId)
          .pipe(map((data) => data.result))
      );
      if (this.resendCounts?.statusCounts) {
        this.resendCounts.statusCounts = this.resendCounts.statusCounts.filter(
          (sc) =>
            !['Pedido suspenso', 'Substituído', 'Falha na entrega'].includes(
              sc.statusName
            )
        );
        this.resendCounts.statusCounts.sort((a, b) => {
          const orderA = this.resendStatusOrder[a.statusName] ?? 99;
          const orderB = this.resendStatusOrder[b.statusName] ?? 99;
          return orderA - orderB;
        });
      }
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    this.resendLoading = false;
  }

  private async loadResendAlertCounts(): Promise<void> {
    this.resendAlertLoading = true;
    try {
      this.resendAlertCounts = await lastValueFrom(
        this.compositionService
          .findResendAlertDashboardCounts(this.form.value.editionId)
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    this.resendAlertLoading = false;
  }

  async findEditions(): Promise<void> {
    try {
      this.editions = await lastValueFrom(
        this.editionService
          .findEditionsBySubscriptionId(this.form.value.subscriptionId || 1)
          .pipe(map((data) => data.result))
      );
      if (this.form.value.subscriptionId === 0) {
        this.editions?.forEach(
          (e) => (e.editionId = EditionId.yearMonthReference(e.editionId || 0))
        );
      }
    } catch (error) {
      this.editions = [];
    }
  }

  async changeSubscription(): Promise<void> {
    LoaderService.showLoader();
    const form = { ...this.form.value };
    this.form.controls['editionId'].reset(
      Number(
        (form.subscriptionId || 1).toString() +
          EditionId.yearMonthReference(form.editionId)
      )
    );
    this.navigate();
    LoaderService.showLoader(false);
  }

  async changeEdition(): Promise<void> {
    LoaderService.showLoader();
    this.navigate();
    LoaderService.showLoader(false);
  }

  editionDate(editionId: number): string {
    return FormUtil.editionDate(editionId);
  }

  editionDateName(editionId: number): string {
    return (
      this.datePipe.transform(
        new Date(
          `${EditionId.year(editionId)}-${EditionId.month(editionId).toString().padStart(2, '0')}-02`
        ),
        'MMMM/yyyy'
      ) || this.editionDate(editionId)
    );
  }

  get edition(): Edition | undefined {
    return this.editions?.find(
      (e) => e.editionId === this.form.value.editionId
    );
  }

  get totalSubscribers(): number {
    return (
      this.editionStatusTotals?.reduce(
        (sum, t) => sum + (t.subscribers || 0),
        0
      ) || 0
    );
  }

  get pending(): number {
    return (
      this.editionStatusTotals?.reduce(
        (sum, t) => sum + (t.pending || 0),
        0
      ) || 0
    );
  }

  subscriberStatusColor(subscriberStatus: number): string {
    return FormUtil.subscriberStatusColor(subscriberStatus);
  }

  async findSubscribers(
    statusId: number,
    status: string = null
  ): Promise<void> {
    LoaderService.showLoader();
    try {
      const subscribers = await lastValueFrom(
        this.compositionsDashboardService
          .findSubscribersBySubscriberEditionStatusAndEditionId(
            statusId,
            this.form.value.editionId
          )
          .pipe(map((data) => data.result))
      );
      this.dialogService.open(TableModalComponent, {
        width: '80%',
        height: '90vh',
        header: status
          ? 'Assinantes com status ' + status
          : 'Assinantes da edição ' + (this.edition?.theme || ''),
        maximizable: true,
        data: {
          cols: this.subscriberColumns(statusId),
          elements: subscribers,
          modelName: 'assinantes',
          showHeader: true,
          showTitle: false,
          addButton: false,
          exportPdf: false,
          dropdownFilters: {
            subscriberEditionStatusName: subscribers.reduce(
              (list: Array<DropdownFilter>, s) => {
                if (
                  !list.some((ss) => ss.value === s.subscriberEditionStatusName)
                ) {
                  list.push({
                    label: s.subscriberEditionStatusName,
                    value: s.subscriberEditionStatusName
                  });
                }
                list.sort((c1, c2) => {
                  if (c1.value > c2.value) return 1;
                  if (c1.value < c2.value) return -1;
                  return 0;
                });
                return list;
              },
              []
            ),
            statusMillenium: subscribers.reduce(
              (list: Array<DropdownFilter>, s) => {
                if (!list.some((ss) => ss.value === s.statusMillenium)) {
                  list.push({
                    label: s.statusMillenium,
                    value: s.statusMillenium
                  });
                }
                list.sort((c1, c2) => {
                  if (c1.value > c2.value) return 1;
                  if (c1.value < c2.value) return -1;
                  return 0;
                });
                return list;
              },
              []
            )
          }
        }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  subscriberColumns(statusId: number): Array<TableColumn> {
    let cols: Array<TableColumn>;
    if (!this.form.value.subscriptionId) {
      cols = [
        new TableColumn('Id', 'subscriberId', true, 'number', '/users/subscribers/', 'subscriberId'),
        new TableColumn('Assinatura', 'subscriptionName', true, 'text', undefined, undefined, true, 'contains'),
        new TableColumn('GlamId', 'boxId', true, 'text', '/users/subscribers/', 'subscriberId'),
        new TableColumn('Assinante', 'personName', true, 'text', '/users/person/', 'personId', true, 'contains'),
        new TableColumn('Status ', 'subscriberEditionStatusName', true, 'text', undefined, undefined, true, 'contains')
      ];
    } else {
      cols = [
        new TableColumn('Id', 'subscriberId', true, 'number', '/users/subscribers/', 'subscriberId'),
        new TableColumn('GlamId', 'boxId', true, 'text', '/users/subscribers/', 'subscriberId'),
        new TableColumn('Assinante', 'personName', true, 'text', '/users/person/', 'personId', true, 'contains')
      ];
    }
    if (statusId) {
      cols = cols.concat([
        new TableColumn('Status Millennium', 'statusMillenium', true, 'text', undefined, undefined, true, 'contains'),
        new TableColumn('Últ atualização', 'dateUpdatedMillenium', true, 'date', undefined, undefined, true, 'between')
      ]);
    }
    return cols;
  }

  async findResendOrders(sc: ResendStatusCount): Promise<void> {
    LoaderService.showLoader();
    try {
      const orders = await lastValueFrom(
        this.compositionService
          .findResendOrdersByStatus(this.form.value.editionId, sc.statusName)
          .pipe(map((data) => data.result))
      );
      this.dialogService.open(TableModalComponent, {
        width: '80%',
        height: '90vh',
        header: 'Reenvios - ' + sc.statusName,
        maximizable: true,
        data: {
          cols: [
            new TableColumn('Pedido', 'shippingOrderId', true, 'number', '/products/shipping-orders/', 'shippingOrderId', true, 'equals'),
            new TableColumn('Nome', 'personName', true, 'text', '/users/person/', 'personId', true, 'contains'),
            new TableColumn('Email', 'personEmail', true, 'text', undefined, undefined, true, 'contains'),
            new TableColumn('Rastreio', 'trackingCode', true, 'text', undefined, undefined, true, 'contains'),
            new TableColumn('Criado em', 'dateCreated', true, 'date', undefined, undefined, true, 'between'),
            new TableColumn('Atualizado em', 'dateUpdated', true, 'date', undefined, undefined, true, 'between')
          ],
          elements: orders,
          modelName: 'reenvios',
          showHeader: true,
          showTitle: false,
          addButton: false,
          exportPdf: false
        }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  async findAlertOrders(sc: ResendStatusCount): Promise<void> {
    LoaderService.showLoader();
    try {
      const hasTrackingStatus = sc.statusName === 'Entregas com ocorrências' || sc.statusName === 'Entregas com falhas';
      let observable: any;
      switch (sc.statusName) {
        case 'Em atraso':
          observable = this.compositionService.findLateOrders(this.form.value.editionId);
          break;
        case 'Sem movimentação':
          observable = this.compositionService.findNoMovementOrders(this.form.value.editionId);
          break;
        case 'Entregas com ocorrências':
          observable = this.compositionService.findOccurrenceOrders(this.form.value.editionId);
          break;
        case 'Entregas com falhas':
          observable = this.compositionService.findFailureOrders(this.form.value.editionId);
          break;
        default:
          return;
      }

      const orders = await lastValueFrom(
        observable.pipe(map((data: any) => data.result))
      );

      const cols = [
        new TableColumn('Pedido', 'shippingOrderId', true, 'number', '/products/shipping-orders/', 'shippingOrderId', true, 'equals'),
        new TableColumn('Nome', 'personName', true, 'text', '/users/person/', 'personId', true, 'contains'),
        new TableColumn('Email', 'personEmail', true, 'text', undefined, undefined, true, 'contains'),
        new TableColumn('Rastreio', 'trackingCode', true, 'text', undefined, undefined, true, 'contains'),
        ...(hasTrackingStatus ? [new TableColumn('Status Transportadora', 'lastTrackingStatus', true, 'text', undefined, undefined, true, 'contains')] : []),
        new TableColumn('Criado em', 'dateCreated', true, 'date', undefined, undefined, true, 'between'),
        new TableColumn('Atualizado em', 'dateUpdated', true, 'date', undefined, undefined, true, 'between')
      ];

      this.dialogService.open(TableModalComponent, {
        width: '80%',
        height: '90vh',
        header: 'Alertas Edição - ' + sc.statusName,
        maximizable: true,
        data: { cols, elements: orders, modelName: 'alertas', showHeader: true, showTitle: false, addButton: false, exportPdf: false }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  async findResendAlertOrders(sc: ResendStatusCount): Promise<void> {
    LoaderService.showLoader();
    try {
      const hasTrackingStatus = sc.statusName === 'Entregas com ocorrências' || sc.statusName === 'Entregas com falhas';
      let observable: any;
      switch (sc.statusName) {
        case 'Em atraso':
          observable = this.compositionService.findResendLateOrders(this.form.value.editionId);
          break;
        case 'Sem movimentação':
          observable = this.compositionService.findResendNoMovementOrders(this.form.value.editionId);
          break;
        case 'Entregas com ocorrências':
          observable = this.compositionService.findResendOccurrenceOrders(this.form.value.editionId);
          break;
        case 'Entregas com falhas':
          observable = this.compositionService.findResendFailureOrders(this.form.value.editionId);
          break;
        default:
          return;
      }

      const orders = await lastValueFrom(
        observable.pipe(map((data: any) => data.result))
      );

      const cols = [
        new TableColumn('Pedido', 'shippingOrderId', true, 'number', '/products/shipping-orders/', 'shippingOrderId', true, 'equals'),
        new TableColumn('Nome', 'personName', true, 'text', '/users/person/', 'personId', true, 'contains'),
        new TableColumn('Email', 'personEmail', true, 'text', undefined, undefined, true, 'contains'),
        new TableColumn('Rastreio', 'trackingCode', true, 'text', undefined, undefined, true, 'contains'),
        ...(hasTrackingStatus ? [new TableColumn('Status Transportadora', 'lastTrackingStatus', true, 'text', undefined, undefined, true, 'contains')] : []),
        new TableColumn('Criado em', 'dateCreated', true, 'date', undefined, undefined, true, 'between'),
        new TableColumn('Atualizado em', 'dateUpdated', true, 'date', undefined, undefined, true, 'between')
      ];

      this.dialogService.open(TableModalComponent, {
        width: '80%',
        height: '90vh',
        header: 'Alertas Reenvio - ' + sc.statusName,
        maximizable: true,
        data: { cols, elements: orders, modelName: 'alertas', showHeader: true, showTitle: false, addButton: false, exportPdf: false }
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }
}
